<script setup lang="ts">
import { isPortalFeatureEnabled } from '@/environment/environment'
import { useDisplay } from 'vuetify'
import { VMaskInput } from 'vuetify/labs/VMaskInput'
import { storeToRefs } from 'pinia'
import { useStaffStore } from '../stores/staffStore'
import type { TEmployeeStatus } from '../types/IStaffEmployee'
import { parseFullName } from '@/utils/formatters'
import { useApiStore } from '@/modules/apiStore/stores/apiStore'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { usePermissionsStore } from '@/modules/permissions/stores/permissionsStore'
import NewTreeComponent from '@/components/createdUi/NewTreeComponent.vue'
import { searchDepartments } from '@/components/createdUi/treeSearch'
import DateMaskInput from '@/components/createdUi/DateMaskInput.vue'
import ReportTemplateMenu from '@/components/createdUi/ReportTemplateMenu.vue'
import { STAFF_ANCHORS } from '@/modules/onboarding/scenarios/staff/anchors'

const props = defineProps<{
  modelValue: boolean
  employeeId: string | null
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
}>()

const { smAndDown } = useDisplay()

const staffStore = useStaffStore()
const permissionsStore = usePermissionsStore()
const { currentEmployee } = storeToRefs(staffStore)
const apiStore = useApiStore()
const feedbackStore = useFeedbackStore()

// Состояния видимости оверлеев выбора
const showDepartmentSelect = ref(false)
const showPositionMenu = ref(false)

// Состояния диалогов подтверждения
const isConfirmCancelOpen = ref(false)

// Данные формы
const lastName = ref('')
const firstName = ref('')
const middleName = ref('')
const birthDate = ref('')
const email = ref('')
const phoneNumber = ref('')
const workPhoneNumber = ref('')
const status = ref<TEmployeeStatus>('Активен')

const selectedDepartmentId = ref<string | null>(null)
const selectedPositionId = ref<string | null>(null)

// Список должностей в формате дерева для FilterTreeSelect
const positionsTree = computed(() => {
  return staffStore.positionsList
    .filter(pos => pos.id !== 'empty')
    .map(pos => ({
      key: pos.id,
      label: pos.name || 'Не указано',
      data: {
        name: pos.name || 'Не указано',
        selfId: pos.id,
      },
      children: [],
    }))
})

// Обработчики выбора
function onDepartmentSelect(value: { id: string } | null) {
  selectedDepartmentId.value = value ? value.id : null
}

function onPositionSelect(value: { id: string } | null) {
  selectedPositionId.value = value ? value.id : null
  showPositionMenu.value = false
}

// Текст статуса сотрудника для чипа
const statusText = computed(() => {
  if (status.value === 'Не активен') return 'Заблокирован'
  if (status.value === 'Уволен') return 'Уволен'
  return 'Активен'
})

// Классы цвета статуса для чипа
const statusColorClass = computed(() => {
  if (status.value === 'Не активен') return 'bg-label-error text-error'
  if (status.value === 'Уволен') return 'bg-label-secondary text-secondary'
  return 'bg-label-success text-success'
})

const rolesList = ref<Array<{ label: string; value: string; description: string; checked: boolean }>>([])

// Разделение ролей на 2 колонки
const leftColumnRoles = computed(() => {
  const mid = Math.ceil(rolesList.value.length / 2)
  return rolesList.value.slice(0, mid).map((role, idx) => ({ ...role, originalIndex: idx }))
})

const rightColumnRoles = computed(() => {
  const mid = Math.ceil(rolesList.value.length / 2)
  return rolesList.value.slice(mid).map((role, idx) => ({ ...role, originalIndex: mid + idx }))
})

function handleRoleCheck(index: number) {
  rolesList.value[index].checked = !rolesList.value[index].checked
}

// Заполнение формы при открытии
watch(() => [props.modelValue, props.employeeId], async ([isOpen, empId]) => {
  if (isOpen) {
    if (!apiStore.departmentsTree || !apiStore.departmentsTree.length) {
      await apiStore.fetchAllDepartments()
    }
    if (!staffStore.positionsList || !staffStore.positionsList.length) {
      await staffStore.fetchPositions()
    }
    if (staffStore.rolesList.length === 0) {
      await staffStore.fetchRoles()
    }
    if (empId) {
      staffStore.currentEmployee = null
      lastName.value = ''
      firstName.value = ''
      middleName.value = ''
      birthDate.value = ''
      email.value = ''
      phoneNumber.value = ''
      selectedDepartmentId.value = null
      selectedPositionId.value = null
      status.value = 'Активен'
      rolesList.value = []

      const emp = await staffStore.fetchEmployeeById(empId as string)
      if (emp) {
        if (!emp.last_name && emp.full_name) {
          const parsed = parseFullName(emp.full_name)
          lastName.value = parsed.last_name
          firstName.value = parsed.first_name
          middleName.value = parsed.middle_name
        } else {
          lastName.value = emp.last_name || ''
          firstName.value = emp.first_name || ''
          middleName.value = emp.middle_name || ''
        }
        birthDate.value = emp.birth_date || ''
        email.value = emp.email || ''
        phoneNumber.value = emp.phone_number || ''
        workPhoneNumber.value = emp.work_phone_number || ''
        selectedDepartmentId.value = emp.department?.id || emp.department_id || null
        selectedPositionId.value = emp.position?.id || emp.position_id || null
        status.value = emp.status

        const empRoleSubs = (emp.roles || []).map(r => r.sub)
        rolesList.value = staffStore.rolesList.map(role => ({
          label: role.name,
          value: role.sub,
          description: role.description || '',
          checked: empRoleSubs.includes(role.sub),
        }))
      }
    }
  }
}, { immediate: true })

// Проверка доступности редактирования конкретного поля
const canEditField = (fieldName: string): boolean => {
  if (!currentEmployee.value) return true
  if (!currentEmployee.value.editable_fields) return true
  return currentEmployee.value.editable_fields.includes(fieldName)
}

// Проверка валидности формы
const isFormValid = computed(() => {
  return (
    lastName.value.trim() !== ''
    && firstName.value.trim() !== ''
    && phoneNumber.value.trim() !== ''
    && selectedDepartmentId.value !== null
    && selectedPositionId.value !== null
  )
})

// Попытка закрыть диалог
function handleCloseClick() {
  // Проверяем, изменились ли данные по сравнению с исходными
  const emp = currentEmployee.value
  if (!emp) {
    emit('update:modelValue', false)
    return
  }

  const empRoleSubs = (emp.roles || []).map(r => r.sub).sort().join(',')
  const currentRoleSubs = rolesList.value.filter(r => r.checked).map(r => r.value).sort().join(',')
  const isRolesChanged = empRoleSubs !== currentRoleSubs

  const isChanged
    = lastName.value.trim() !== (emp.last_name || '')
    || firstName.value.trim() !== (emp.first_name || '')
    || middleName.value.trim() !== (emp.middle_name || '')
    || birthDate.value !== (emp.birth_date || '')
    || email.value.trim() !== (emp.email || '')
    || phoneNumber.value.trim() !== (emp.phone_number || '')
    || workPhoneNumber.value.trim() !== (emp.work_phone_number || '')
    || selectedDepartmentId.value !== (emp.department?.id || emp.department_id || null)
    || selectedPositionId.value !== (emp.position?.id || emp.position_id || null)
    || isRolesChanged

  if (isChanged) {
    isConfirmCancelOpen.value = true
  } else {
    emit('update:modelValue', false)
  }
}

// Подтверждение выхода
function confirmExit() {
  isConfirmCancelOpen.value = false
  emit('update:modelValue', false)
}

// Отмена выхода
function cancelExit() {
  isConfirmCancelOpen.value = false
}

/**
 * Сохраняет изменения профиля сотрудника с проверкой прав доступа (modules.employee_management.employee_edit).
 */
async function submitForm() {
  if (!isFormValid.value || !props.employeeId) return

  if (!permissionsStore.modules.employee_management?.employee_edit) {
    feedbackStore.showToast({
      type: 'error',
      message: 'У вас нет прав на редактирование данных сотрудника',
      time: 5000,
    })
    return
  }

  try {
    await staffStore.editEmployee(props.employeeId, {
      last_name: lastName.value.trim(),
      first_name: firstName.value.trim(),
      middle_name: middleName.value.trim(),
      birth_date: birthDate.value || null,
      email: email.value.trim(),
      phone_number: phoneNumber.value.trim(),
      work_phone_number: workPhoneNumber.value.trim() || null,
      department: selectedDepartmentId.value ? { id: selectedDepartmentId.value } : null,
      position: selectedPositionId.value ? { id: selectedPositionId.value } : null,
      roles: rolesList.value.filter(r => r.checked).map(r => ({ sub: r.value })),
    })
    emit('update:modelValue', false)
  } catch (error) {}
}

// Реестр электронных подписей, отфильтрованный по этому сотруднику
const signaturesRoute = computed(() => ({
  name: 'ELECTRONIC_SIGNATURES',
  params: { collection: 'requirements' },
  query: { employee: props.employeeId },
}))
</script>

<template>
  <!-- Blur-подложка для embedded-режима -->
  <Teleport to="body">
    <div
      v-if="modelValue && !smAndDown"
      class="entity-form-backdrop"
    />
  </Teleport>

  <!-- Кнопка закрытия в стиле "ушек" как в TicketForm -->
  <Teleport to="body">
    <div
      v-if="modelValue && !smAndDown"
      class="entity-form-actions"
    >
      <ReportTemplateMenu
        v-if="employeeId && permissionsStore.modules.employee_management?.employee_view"
        object-type="employee"
        :object-id="employeeId"
        variant="ear"
      />
      <VTooltip
        v-if="employeeId && isPortalFeatureEnabled('electronic_signatures')"
        location="bottom"
        text="Электронные подписи"
      >
        <template #activator="{ props: tooltipProps }">
          <VBtn
            v-bind="tooltipProps"
            icon="ri-key-2-line"
            color="primary"
            class="entity-form-action-btn"
            :data-onboarding="STAFF_ANCHORS.edit.signatures"
            aria-label="Электронные подписи"
            :to="signaturesRoute"
            @click="emit('update:modelValue', false)"
          />
        </template>
      </VTooltip>
      <VBtn
        icon="ri-close-line"
        color="primary"
        class="entity-form-action-btn"
        aria-label="Закрыть"
        @click="handleCloseClick"
      />
    </div>
  </Teleport>

  <VDialog
    :model-value="modelValue"
    :max-width="smAndDown ? '100%' : 700"
    :scrim="smAndDown"
    :fullscreen="smAndDown"
    persistent
    no-click-animation
    :content-class="smAndDown ? 'edit-employee-dialog-mobile' : 'entity-form-embedded edit-employee-dialog'"
  >
    <VCard
      class="d-flex flex-column bg-surface"
      :class="{ 'entity-form-card h-100': !smAndDown }"
      :style="smAndDown ? 'padding: 20px !important; min-height: 100vh;' : 'padding: 20px 32px !important;'"
    >
      <div v-if="modelValue && smAndDown && employeeId" class="d-flex justify-end align-center gap-1 mr-12">
        <VTooltip
          v-if="isPortalFeatureEnabled('electronic_signatures')"
          location="bottom"
          text="Электронные подписи"
        >
          <template #activator="{ props: tooltipProps }">
            <VBtn
              v-bind="tooltipProps"
              icon="ri-key-2-line"
              variant="text"
              color="secondary"
              class="flex-shrink-0 hover-primary"
              :data-onboarding="STAFF_ANCHORS.edit.signatures"
              aria-label="Электронные подписи"
              :to="signaturesRoute"
              @click="emit('update:modelValue', false)"
            />
          </template>
        </VTooltip>
        <ReportTemplateMenu v-if="permissionsStore.modules.employee_management?.employee_view" object-type="employee" :object-id="employeeId" />
      </div>
      <!-- Крестик закрытия для мобильных -->
      <VBtn
        v-if="smAndDown"
        icon="ri-close-line"
        variant="text"
        density="comfortable"
        class="close-dialog-btn"
        style="position: absolute; z-index: 10; top: 16px; right: 20px;"
        @click="handleCloseClick"
      />

      <div class="edit-employee-header d-flex flex-column gap-1 flex-shrink-0 px-4 px-sm-6 pb-6">
        <h2 class="text-h4 font-weight-medium edit-employee-title">
          Карточка сотрудника
        </h2>
        <div class="d-flex">
          <VChip
            size="small"
            class="rounded-pill font-weight-medium"
            :class="statusColorClass"
            variant="tonal"
          >
            {{ statusText }}
          </VChip>
        </div>
      </div>

      <!-- Скроллируемая область контента (использует класс из _entity-form-embedded.scss) -->
      <div class="entity-form-scroll-body flex-grow-1 pt-3 pr-1">
        <!-- Поля формы -->
        <div class="d-flex flex-column gap-5">
          <VTextField
            v-model="lastName"
            :data-onboarding="STAFF_ANCHORS.edit.personalFirst"
            variant="outlined"
            hide-details
            class="custom-field"
            :disabled="!canEditField('last_name')"
            persistent-placeholder
          >
            <template #label>
              <span>Фамилия<sup class="required-label">*</sup></span>
            </template>
          </VTextField>
          <VTextField
            v-model="firstName"
            variant="outlined"
            hide-details
            class="custom-field"
            :disabled="!canEditField('first_name')"
            persistent-placeholder
          >
            <template #label>
              <span>Имя<sup class="required-label">*</sup></span>
            </template>
          </VTextField>
          <VTextField
            v-model="middleName"
            label="Отчество"
            variant="outlined"
            hide-details
            class="custom-field"
            :disabled="!canEditField('middle_name')"
          />

          <DateMaskInput
            v-model="birthDate"
            class="custom-field-date"
            :disabled="!canEditField('birth_date')"
          >
            <template #label>
              <span>Дата рождения</span>
            </template>
          </DateMaskInput>

          <div
            class="d-flex gap-5"
            :class="smAndDown ? 'flex-column' : ''"
          >
            <VTextField
              v-model="email"
              label="Электронная почта"
              variant="outlined"
              density="default"
              hide-details
              class="custom-field flex-grow-1"
              :class="smAndDown ? 'w-100' : 'w-50'"
              :disabled="!canEditField('email')"
            />
            <VMaskInput
              v-model="phoneNumber"
              mask="+7 (###) ###-##-##"
              variant="outlined"
              hide-details
              class="custom-field flex-grow-1"
              :class="smAndDown ? 'w-100' : 'w-50'"
              :disabled="!canEditField('phone_number')"
            >
              <template #label>
                <span>Личный телефон<sup class="required-label">*</sup></span>
              </template>
            </VMaskInput>
          </div>

          <div
            class="d-flex gap-5"
            :class="smAndDown ? 'flex-column' : ''"
            :data-onboarding="STAFF_ANCHORS.edit.personalLast"
          >
            <VTextField
              v-model="workPhoneNumber"
              label="Служебный телефон"
              variant="outlined"
              density="default"
              hide-details
              class="custom-field flex-grow-1 w-100"
              :disabled="!canEditField('work_phone_number')"
            />
          </div>

          <!-- Разделитель ДОЛЖНОСТЬ И СТРУКТУРА -->
          <CustomDivider title="Должность и структура" />

          <!-- Выбор Подразделения и Должности; обёртка — общий якорь онбординга для обоих полей -->
          <div class="d-flex flex-column gap-5" :data-onboarding="STAFF_ANCHORS.edit.structure">
          <NewTreeComponent
            v-model:visible="showDepartmentSelect"
            placeholder="Выберите подразделение"
            label="Подразделение"
            is-label-required
            search-placeholder="Поиск по подразделениям"
            :selected="{ value: selectedDepartmentId ? { id: selectedDepartmentId } : null }"
            :tree-data="apiStore.departmentsTree"
            :search-tree="searchDepartments"
            :disabled="!canEditField('department')"
            @update:selected="onDepartmentSelect"
          />

          <NewTreeComponent
            v-model:visible="showPositionMenu"
            placeholder="Выберите должность"
            label="Должность"
            is-label-required
            search-placeholder="Поиск по должностям"
            :selected="{ value: selectedPositionId ? { id: selectedPositionId } : null }"
            :tree-data="positionsTree"
            :disabled="!canEditField('position')"
            @update:selected="onPositionSelect"
          />
          </div>

          <!-- Разделитель РОЛИ -->
          <CustomDivider v-if="permissionsStore.modules.employee_management?.role_edit" title="Роли" />

          <div v-if="permissionsStore.modules.employee_management?.role_edit" class="roles-grid" :data-onboarding="STAFF_ANCHORS.edit.roles">
            <div class="role-column gap-2">
              <VCheckbox
                v-for="role in leftColumnRoles"
                :key="role.value"
                :model-value="role.checked"
                color="primary"
                hide-details
                class="custom-checkbox"
                :disabled="!canEditField('roles')"
                @update:model-value="handleRoleCheck(role.originalIndex)"
              >
                <template #label>
                  <VTooltip v-if="role.description" location="top">
                    <template #activator="{ props: tooltipProps }">
                      <span v-bind="tooltipProps">{{ role.label }}</span>
                    </template>
                    {{ role.description }}
                  </VTooltip>
                  <span v-else>{{ role.label }}</span>
                </template>
              </VCheckbox>
            </div>
            <div class="role-column gap-2">
              <VCheckbox
                v-for="role in rightColumnRoles"
                :key="role.value"
                :model-value="role.checked"
                color="primary"
                hide-details
                class="custom-checkbox"
                :disabled="!canEditField('roles')"
                @update:model-value="handleRoleCheck(role.originalIndex)"
              >
                <template #label>
                  <VTooltip v-if="role.description" location="top">
                    <template #activator="{ props: tooltipProps }">
                      <span v-bind="tooltipProps">{{ role.label }}</span>
                    </template>
                    {{ role.description }}
                  </VTooltip>
                  <span v-else>{{ role.label }}</span>
                </template>
              </VCheckbox>
            </div>
          </div>
        </div>
      </div>

      <!-- Футер с кнопкой Сохранить -->
      <div class="d-flex align-center pt-5 pb-5 mt-auto border-t" :class="smAndDown ? 'justify-center w-100' : 'justify-end'" style="border-color: rgba(var(--v-theme-on-surface), 0.12) !important; height: 88px;">
        <VBtn
          color="primary"
          class="action-btn save-btn"
          :class="{ 'w-100': smAndDown }"
          :data-onboarding="STAFF_ANCHORS.edit.save"
          :disabled="!isFormValid"
          @click="submitForm"
        >
          Сохранить
        </VBtn>
      </div>
    </VCard>
  </VDialog>

  <!-- Диалог подтверждения выхода -->
  <VDialog
    v-model="isConfirmCancelOpen"
    max-width="560"
    persistent
    no-click-animation
    class="confirm-exit-dialog"
    content-class="entity-form-menu"
  >
    <VCard class="d-flex flex-column bg-surface" style="padding: 0 !important; border-radius: 10px !important; border: 1px solid rgba(var(--v-theme-on-surface), 0.08) !important; width: 560px !important; height: 166px !important;">
      <!-- Шапка -->
      <div style="padding: 20px !important; height: 68px !important;">
        <h3 style="font-family: 'Inter', sans-serif !important; font-size: 18px !important; font-weight: 500 !important; color: rgba(var(--v-theme-on-surface), 0.9) !important; line-height: 28px !important; margin: 0;">
          Подтверждение
        </h3>
      </div>

      <!-- Тело -->
      <div style="padding: 0 20px 20px !important; height: 40px !important;">
        <p style="font-family: 'Inter', sans-serif !important; font-size: 15px !important; font-weight: 400 !important; color: rgba(var(--v-theme-on-surface), 0.7) !important; line-height: 20px !important; margin: 0;">
          Данные не сохранятся. Вы действительно хотите выйти?
        </p>
      </div>

      <!-- Подвал -->
      <div style="padding: 0 20px 20px !important; height: 58px !important;">
        <div class="d-flex justify-end gap-4">
          <VBtn
            variant="outlined"
            class="action-btn back-btn confirm-btn-cancel"
            @click="cancelExit"
          >
            Отменить
          </VBtn>
          <VBtn
            color="primary"
            class="action-btn next-btn confirm-btn-exit"
            @click="confirmExit"
          >
            Выйти
          </VBtn>
        </div>
      </div>
    </VCard>
  </VDialog>
</template>

<style scoped lang="scss">
.close-dialog-btn {
  color: rgba(var(--v-theme-on-surface), 0.45) !important;

  &:hover {
    color: rgba(var(--v-theme-on-surface), 0.9) !important;
  }
}

/* Стили для боковой панели на десктопе */
:global(.edit-employee-dialog.entity-form-embedded) {
  inline-size: 700px !important;
  min-inline-size: auto !important; /* Перебиваем минимальные 1000px бесчатового режима */
  inset-inline-start: auto !important; /* Прижимаем к правому краю */
  box-shadow: 0 4px 10px 0 rgba(46, 38, 61, 0.2) !important;
  border-radius: 6px 0 0 6px !important;
  border: none !important;
}

:global(.edit-employee-dialog.entity-form-embedded .v-card) {
  border-radius: 6px 0 0 6px !important;
}

.edit-employee-header {
  padding-inline-end: calc(1.5rem + 48px);
}

@media (max-width: 599px) {
  .edit-employee-header {
    padding-inline-end: calc(1rem + 48px);
  }
}

.edit-employee-title {
  color: rgba(var(--v-theme-text-primary), var(--v-high-emphasis-opacity)) !important;
  font-feature-settings: 'liga' off, 'clig' off;
}

.custom-field,
.custom-field-date {
  :deep(.v-field__control) {
    border-radius: 6px !important;
    height: 48px !important;
    background-color: transparent !important;

    &.v-field--focused {
      border-color: rgb(var(--v-theme-primary)) !important;
    }
  }

  :deep(.v-field__input) {
    padding-inline: 16px !important;
    font-family: 'Inter', sans-serif !important;
    font-size: 15px !important;
    font-weight: 400 !important;
    height: 48px !important;
    display: flex;
    align-items: center;
    color: rgba(var(--v-theme-on-surface), 0.9) !important;

    &::placeholder {
      color: rgba(var(--v-theme-text-primary), var(--v-disabled-opacity)) !important;
      opacity: 1;
    }
  }

  :deep(.v-field-label) {
    font-family: 'Inter', sans-serif !important;
    font-size: 13px !important;
    font-weight: 600 !important;
    color: rgba(var(--v-theme-on-surface), 0.7) !important;
  }

  :deep(.v-field-label:not(.v-field-label--floating)) {
    color: rgba(var(--v-theme-text-primary), var(--v-disabled-opacity)) !important;
    font-size: 15px !important;
    font-weight: 400 !important;
  }
}

.custom-select {
  :deep(.v-field__append-inner) {
    align-self: center !important;
    padding-top: 0 !important;
  }
}

.action-btn {
  font-family: 'Inter', sans-serif !important;
  font-size: 15px !important;
  font-weight: 500 !important;
  height: 38px !important;
  border-radius: 6px !important;
  text-transform: none !important;
  letter-spacing: normal !important;
  box-shadow: none !important;
  padding: 8px 18px !important;
}

.save-btn {
  background-color: rgb(var(--v-theme-primary)) !important;
  color: #ffffff !important;
  min-width: 116px !important;
}

.back-btn {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12) !important;
  color: rgba(var(--v-theme-on-surface), 0.55) !important;
  background-color: transparent !important;
  min-width: 102px !important;

  &:hover {
    background-color: rgba(var(--v-theme-on-surface), 0.04) !important;
  }
}

.confirm-btn-exit {
  background-color: rgb(var(--v-theme-primary)) !important;
  color: #ffffff !important;
  min-width: 88px !important;
}

.confirm-btn-cancel {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12) !important;
  color: rgba(var(--v-theme-on-surface), 0.55) !important;
  background-color: transparent !important;
  min-width: 102px !important;
}

:global(.edit-employee-select-menu) {
  max-width: 0 !important;
}

.custom-checkbox {
  :deep(.v-selection-control) {
    min-height: auto !important;
    height: 36px !important;
  }

  :deep(.v-label) {
    font-family: 'Inter', sans-serif !important;
    font-size: 15px !important;
    font-weight: 400 !important;
    color: rgba(var(--v-theme-on-surface), 0.9) !important;
    opacity: 1 !important;
    padding-inline-start: 8px !important;
  }

  :deep(.v-selection-control__input) {
    color: rgb(var(--v-theme-primary)) !important;
  }
}

.roles-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.role-column {
  display: flex;
  flex-direction: column;
}
</style>
